import React, { useState } from 'react';
import axios from 'axios';
import { Config } from '../../config';
import Transition from '../../utils/Transition';

function PasswordResetSidebar({
  sidebarOpen,
  setSidebarOpen,
  selectedUser = null,
}) {
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    }
    
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm the password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    return newErrors;
  };

  const getAxiosConfig = () => ({
    headers: {
      'access_token': `Bearer ${localStorage.getItem('token')}`,
      'Content-Type': 'application/json'
    },
    withCredentials: true
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    
    setIsLoading(true);
    
    try {
      const response = await axios.put(
        `${Config.APIBaseURL}/users/${selectedUser.id}/password`,
        { new_password: formData.password },
        getAxiosConfig()
      );
      
      if (response.status === 200) {
        setSidebarOpen(false);
        setFormData({ password: '', confirmPassword: '' });
        // You might want to add a success notification here
      }
    } catch (error) {
      if (error.response?.status === 403) {
        setErrors({ general: 'Not authorized to reset password' });
      } else if (error.response?.status === 404) {
        setErrors({ general: 'User not found' });
      } else if (error.response?.status === 400) {
        const detail = error.response.data.detail;
        if (Array.isArray(detail)) {
          setErrors({ password: detail.join(', ') });
        } else {
          setErrors({ password: 'Password does not meet requirements' });
        }
      } else {
        setErrors({ general: 'Error resetting password. Please try again.' });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Transition
      show={sidebarOpen}
      tag="div"
      className="fixed inset-0 overflow-hidden z-50"
      enter="transition ease-in-out duration-200"
      enterStart="opacity-0"
      enterEnd="opacity-100"
      leave="transition ease-in-out duration-200"
      leaveStart="opacity-100"
      leaveEnd="opacity-0"
    >
      <div className="absolute inset-0 overflow-hidden">
        <Transition
          show={sidebarOpen}
          tag="div"
          className="absolute inset-0 bg-slate-900 bg-opacity-30"
          enter="transition ease-out duration-200"
          enterStart="opacity-0"
          enterEnd="opacity-100"
          leave="transition ease-out duration-200"
          leaveStart="opacity-100"
          leaveEnd="opacity-0"
          onClick={() => setSidebarOpen(false)}
        />
        
        <div className="fixed inset-y-0 right-0 flex max-w-full pl-10 sm:pl-16">
          <Transition
            show={sidebarOpen}
            tag="div"
            className="w-screen max-w-md"
            enter="transition ease-in-out duration-500 transform"
            enterStart="translate-x-full"
            enterEnd="translate-x-0"
            leave="transition ease-in-out duration-500 transform"
            leaveStart="translate-x-0"
            leaveEnd="translate-x-full"
          >
            <div className="h-full flex flex-col bg-white dark:bg-slate-800 shadow-xl overflow-y-scroll">
              <div className="px-4 sm:px-6 py-8">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100">
                    Reset Password - {selectedUser?.username}
                  </h2>
                  <button
                    className="text-slate-400 hover:text-slate-500 dark:text-slate-500 dark:hover:text-slate-400"
                    onClick={() => setSidebarOpen(false)}
                  >
                    <span className="sr-only">Close</span>
                    <svg className="w-6 h-6 fill-current" viewBox="0 0 24 24">
                      <path d="M7.95 6.536l4.242-4.243a1 1 0 111.415 1.414L9.364 7.95l4.243 4.242a1 1 0 11-1.415 1.415L7.95 9.364l-4.243 4.243a1 1 0 01-1.414-1.415L6.536 7.95 2.293 3.707a1 1 0 011.414-1.414L7.95 6.536z" />
                    </svg>
                  </button>
                </div>
                
                {errors.general && (
                  <div className="mb-4 px-4 py-2 bg-red-100 dark:bg-red-400/10 text-red-600 dark:text-red-400 rounded">
                    {errors.general}
                  </div>
                )}
                
                <form onSubmit={handleSubmit}>
                  <div className="space-y-4">
                    {/* New Password */}
                    <div>
                      <label className="block text-sm font-medium mb-1" htmlFor="password">
                        New Password <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="password"
                        name="password"
                        className={`form-input w-full ${errors.password ? 'border-red-300' : 'border-slate-200 dark:border-slate-700'}`}
                        type="password"
                        value={formData.password}
                        onChange={handleChange}
                      />
                      {errors.password && (
                        <div className="text-xs mt-1 text-red-500">{errors.password}</div>
                      )}
                    </div>
                    
                    {/* Confirm Password */}
                    <div>
                      <label className="block text-sm font-medium mb-1" htmlFor="confirmPassword">
                        Confirm Password <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="confirmPassword"
                        name="confirmPassword"
                        className={`form-input w-full ${errors.confirmPassword ? 'border-red-300' : 'border-slate-200 dark:border-slate-700'}`}
                        type="password"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                      />
                      {errors.confirmPassword && (
                        <div className="text-xs mt-1 text-red-500">{errors.confirmPassword}</div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-end mt-6">
                    <button
                      type="button"
                      className="btn-sm border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 text-slate-600 dark:text-slate-300"
                      onClick={() => setSidebarOpen(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn-sm bg-indigo-500 hover:bg-indigo-600 text-white ml-3"
                      disabled={isLoading}
                    >
                      {isLoading ? 'Resetting...' : 'Reset Password'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </Transition>
        </div>
      </div>
    </Transition>
  );
}

export default PasswordResetSidebar; 